// dwaar/src/components/Dvarapal.jsx
// Repo: terralekha-core | Folder: dwaar/src/components/
// Dvarapal — Sanskrit: द्वारपाल — doorkeeper, gatekeeper.
//   Route guard that verifies an active Supabase session before rendering
//   any protected page. Unauthenticated visitors are sent to /login.
//
// Usage:
//   <Route path="/dashboard" element={<Dvarapal><Kendra /></Dvarapal>} />
//
// Reveal doctrine:
//   Error details from getSession stay in the console. Users see only the
//   redirect to /login — no error copy surfaces here.

import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { getSupabaseClient } from "../lib/supabaseClient.js";
import { BRAND_NAME } from "../lib/brand.js";

const PHASE = Object.freeze({
  CHECKING: "checking",
  PASS:     "pass",
  DENY:     "deny",
});

export default function Dvarapal({ children, requiredRole }) {
  const [phase, setPhase] = useState(PHASE.CHECKING);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const client = getSupabaseClient();
        const { data: { session } } = await client.auth.getSession();
        if (!session) {
          if (!cancelled) setPhase(PHASE.DENY);
          return;
        }
        // No role required — session alone is sufficient
        if (!requiredRole) {
          if (!cancelled) setPhase(PHASE.PASS);
          return;
        }
        // Role required — fetch the user row from public.users
        const { data: userRow } = await client
          .from("users")
          .select("role")
          .eq("id", session.user.id)
          .single();
        const allowed = userRow?.role === requiredRole;
        if (!cancelled) setPhase(allowed ? PHASE.PASS : PHASE.DENY);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("dvarapal: session check failed", err);
        if (!cancelled) setPhase(PHASE.DENY);
      }
    }

    check();
    return () => { cancelled = true; };
  }, [requiredRole]);

  if (phase === PHASE.CHECKING) {
    return (
      <main className="tl-shell">
        <a href="https://terralekha.in" className="tl-wordmark">{BRAND_NAME}</a>
        <p className="tl-sub tl-loading">Loading…</p>
      </main>
    );
  }

  if (phase === PHASE.DENY) {
    return <Navigate to="/login" replace />;
  }

  return children;
}
